import React, { useState } from 'react';
import { Grid, Upload, UploadProps } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import { CloudUploadOutlined } from '@ant-design/icons';

import { CandidateCV } from '../../components/CandidateCV';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { getUploadCandidateFileProps } from '../../utils/uploadCandidateFileProps';
import { CandidateType } from '../candidates/Candidates.types';

const StyledDragger = styled(Dragger)`
  display: flex;
  border: 2px dashed #dbdbdb;
  border-radius: 0.4rem;
  height: 100%;
  width: 100%;

  .ant-upload .ant-upload-drag {
    border: 3px solid red !important;
  }
`;

export const UploadCVContainer = styled(StyledColumn)`
  align-items: center;
  justify-content: center;

  .ant-upload.ant-upload-drag & {
    border: 3px solid red !important;
  }
`;

export const UploadButton = styled.span`
  padding: 0;
  color: #004ba0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
`;

const ReuploadCV = styled(StyledRow)`
  align-items: center;
  gap: 0.5rem;
  height: 100px;
  transform: translate(0, -100px);
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
`;

type CandidatesPersonalDataProps = {
  candidate?: CandidateType;
  cvAttachement?: string;
  height?: number;
  addCvAttachement?: (id: string) => void;
  updateCVAttachement?: (file: string) => void;
  openAttachements?: () => void;
};

export const CandidatesPersonalData: React.FC<CandidatesPersonalDataProps> = ({
  candidate,
  cvAttachement,
  addCvAttachement,
  updateCVAttachement,
  openAttachements,
  height = 100,
}) => {
  const screens = Grid.useBreakpoint();

  const [hovered, setHovered] = useState(false);

  const candidateCV = candidate?.cv?.uuid ?? cvAttachement;

  const [, cvUploadDrop] = useDrop({
    accept: 'email',
    drop: () => ({ name: 'candidate attachments' }),
  });

  const uploadProps: UploadProps = {
    ...getUploadCandidateFileProps(),
    showUploadList: false,
    onChange: (info) => {
      if (info.file.status === 'done') {
        const fileId = info.file.response.uuid;
        updateCVAttachement?.(fileId);
        addCvAttachement?.(fileId);
      }
    },
  };

  return (
    <StyledColumn
      onMouseOver={() => {
        if (screens.xl) {
          setHovered(true);
        }
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <UploadCVContainer
        style={{ backgroundColor: '#F7FAFC', zIndex: 10, height: screens.xl ? `${height}px` : undefined }}
        ref={cvUploadDrop}
      >
        {cvAttachement ? (
          <div style={{ height: '100%', width: '100%' }}>
            <CandidateCV fileId={candidateCV} />
            <ReuploadCV hidden={!hovered}>
              <CloudUploadOutlined style={{ fontSize: '20px', color: '#a8a8a8' }} />
              <div>Upload CV</div>
              <div>
                <Upload {...uploadProps}>
                  <UploadButton>from file</UploadButton>
                </Upload>
              </div>
              {openAttachements && (
                <>
                  <div>or</div>
                  <div>
                    <UploadButton onClick={openAttachements}>from attachement</UploadButton>
                  </div>
                </>
              )}
            </ReuploadCV>
          </div>
        ) : (
          <StyledDragger
            {...uploadProps}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CloudUploadOutlined style={{ fontSize: '40px', color: '#a8a8a8' }} />
              <div>Drop CV file (PDF) to upload here</div>
              <div>or</div>
              <UploadButton>BROWSE</UploadButton>
            </div>
          </StyledDragger>
        )}
      </UploadCVContainer>
    </StyledColumn>
  );
};
